import React, { useEffect, useMemo, useRef, useState } from "react";
import Plot from "react-plotly.js";
import { PlotMouseEvent } from "plotly.js";
import { formatIsoTimestampNYC } from "./utils/utils";

export interface TimelineItem {
  id: number | string;
  generation_timestamp: string | null;
  label: string;
  eventDate?: string;
  numComments?: number;
  colorValue?: number; // colors the marker instead of numComments (e.g. similarity in Correlate)
  detail?: string; // third tooltip line instead of the comment count
}

const PLOT_MARGIN_LR = 10;
// Minimum on-screen pixel gap two markers must keep before we consider them
// "overlapping" and push the later one up a lane. A bit larger than the
// marker's own diameter so they read as visually separate, not just touching.
const OVERLAP_PX = 10;

// Sequential blue ramp (100 -> 700, light -> dark) from the design system's
// magnitude scale, used to encode num_comments from min to max in this series.
const COMMENTS_COLORSCALE: [number, string][] = [
  [0 / 12, "#cde2fb"],
  [1 / 12, "#b7d3f6"],
  [2 / 12, "#9ec5f4"],
  [3 / 12, "#86b6ef"],
  [4 / 12, "#6da7ec"],
  [5 / 12, "#5598e7"],
  [6 / 12, "#3987e5"],
  [7 / 12, "#2a78d6"],
  [8 / 12, "#256abf"],
  [9 / 12, "#1c5cab"],
  [10 / 12, "#184f95"],
  [11 / 12, "#104281"],
  [12 / 12, "#0d366b"],
];

function normalizeTs(ts: string): string {
  return ts.includes("+") || ts.toUpperCase().includes("Z") ? ts : ts + "Z";
}

const NYC_PARTS_FMT = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

// Plotly's date axis reads a Date's UTC fields to lay out ticks/labels,
// ignoring the browser's local timezone entirely. To make the axis read as
// NYC wall-clock time we build a Date whose UTC fields hold the NYC wall-clock
// values (not the real instant) — a display-only stand-in, never used for
// actual time-delta math.
function toNYCPlotDate(ms: number): Date {
  const parts = Object.fromEntries(NYC_PARTS_FMT.formatToParts(new Date(ms)).map((p) => [p.type, p.value]));
  return new Date(
    Date.UTC(
      parseInt(parts.year, 10),
      parseInt(parts.month, 10) - 1,
      parseInt(parts.day, 10),
      parseInt(parts.hour, 10) % 24,
      parseInt(parts.minute, 10),
      parseInt(parts.second, 10)
    )
  );
}

// Greedily assigns each (time-sorted) point to the first lane whose most
// recently placed point is far enough away in time; if none qualifies, opens
// a new lane. Standard interval/calendar-stacking approach.
function assignLanes(msSorted: number[], thresholdMs: number): number[] {
  const laneLastMs: number[] = [];
  const lanes: number[] = [];
  for (const t of msSorted) {
    let assigned = -1;
    for (let lane = 0; lane < laneLastMs.length; lane++) {
      if (t - laneLastMs[lane] >= thresholdMs) {
        assigned = lane;
        break;
      }
    }
    if (assigned === -1) {
      assigned = laneLastMs.length;
      laneLastMs.push(t);
    } else {
      laneLastMs[assigned] = t;
    }
    lanes.push(assigned);
  }
  return lanes;
}

// Strip plot of generation_timestamp values. Points that would render close
// enough on screen to visually overlap are stacked into adjacent lanes
// instead, based on the chart's actual pixel width — so the same time gap
// stacks on a wide chart but not on a narrow one.
export const RedditFeedTimeline = ({
  items,
  onItemClick,
  title = "Generation timeline",
  colorTitle = "Comments",
}: {
  items: TimelineItem[];
  onItemClick?: (id: number | string) => void;
  title?: string;
  colorTitle?: string;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [plotWidth, setPlotWidth] = useState(600);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      // Ignore near-zero reads — a panel mid-resize (or briefly hidden) can
      // report a bogus tiny width; committing it would wrongly persist until
      // the next real resize event fires.
      if (w && w > 50) setPlotWidth(w);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const { x, y, text, ids, colors, cmin, cmax, maxLane } = useMemo(() => {
    const valid = items.filter((i) => !!i.generation_timestamp);
    const sorted = [...valid].sort((a, b) =>
      (a.generation_timestamp as string).localeCompare(b.generation_timestamp as string)
    );

    const msList = sorted.map((item) => new Date(normalizeTs(item.generation_timestamp as string)).getTime());

    // Floor well above 1px — a transient/stale container read (e.g. mid Dockview
    // panel resize) must not be allowed to collapse the pixel budget to ~0, which
    // would blow the overlap threshold up to "everything overlaps" and stack
    // every point into its own lane.
    const innerWidthPx = Math.max(200, plotWidth - PLOT_MARGIN_LR * 2);
    const minMs = msList.length ? Math.min(...msList) : 0;
    const maxMs = msList.length ? Math.max(...msList) : 0;
    // Pad the assumed displayed range a bit to roughly account for Plotly's
    // own autorange padding — slightly overestimating the gap needed is safe.
    const domainMs = Math.max(1, (maxMs - minMs) * 1.1);
    const msPerPixel = domainMs / innerWidthPx;
    const thresholdMs = OVERLAP_PX * msPerPixel;

    const lanes = assignLanes(msList, thresholdMs);
    const maxLaneSeen = lanes.length ? Math.max(...lanes) : 0;

    const commentCounts = sorted.map((item) => item.colorValue ?? item.numComments ?? 0);
    const commentsMin = commentCounts.length ? Math.min(...commentCounts) : 0;
    const commentsMax = commentCounts.length ? Math.max(...commentCounts) : 0;

    const xs: Date[] = [];
    const ys: number[] = [];
    const texts: string[] = [];
    const itemIds: (number | string)[] = [];

    sorted.forEach((item, i) => {
      const ts = item.generation_timestamp as string;
      const firstLine = item.eventDate ? `${item.label} — ${item.eventDate}` : item.label;
      const commentsLine =
        item.detail ?? `${item.numComments ?? 0} comment${item.numComments !== 1 ? "s" : ""}`;

      xs.push(toNYCPlotDate(msList[i]));
      ys.push(lanes[i]);
      texts.push(`${firstLine}<br>${formatIsoTimestampNYC(ts)}<br>${commentsLine}`);
      itemIds.push(item.id);
    });

    return {
      x: xs,
      y: ys,
      text: texts,
      ids: itemIds,
      colors: commentCounts,
      cmin: commentsMin,
      cmax: commentsMax,
      maxLane: maxLaneSeen,
    };
  }, [items, plotWidth]);

  const height = Math.max(90, Math.min(320, 60 + (maxLane + 1) * 16));

  return (
    <div ref={containerRef} className="border border-gray-200 rounded mb-4 p-2 bg-gray-50 overflow-hidden">
      <p className="text-xs text-gray-500 mb-1">
        {title} ({x.length} item{x.length !== 1 ? "s" : ""} shown, NYC time)
      </p>
      {x.length > 0 && (
        // Give Plotly's autosize/responsive logic an explicitly-sized, clipped
        // host — if a resize glitch ever makes it draw larger than intended,
        // it's contained here instead of spilling into the page below.
        <div style={{ height, overflow: "hidden" }}>
          <Plot
            data={[
              {
                x,
                y,
                text,
                customdata: ids,
                type: "scatter",
                mode: "markers",
                marker: {
                  size: 7,
                  opacity: 0.85,
                  color: colors,
                  cmin,
                  cmax,
                  colorscale: COMMENTS_COLORSCALE,
                  colorbar: {
                    title: { text: colorTitle, font: { size: 10 } },
                    thickness: 10,
                    len: 0.9,
                    outlinewidth: 0,
                    tickfont: { size: 9 },
                  },
                },
                hovertemplate: "%{text}<extra></extra>",
              },
            ]}
            layout={{
              height,
              margin: { l: PLOT_MARGIN_LR, r: 70, t: 4, b: 28 },
              xaxis: { type: "date", showgrid: false, fixedrange: false },
              yaxis: { visible: false, range: [-1, maxLane + 1], fixedrange: true },
              showlegend: false,
              paper_bgcolor: "rgba(0,0,0,0)",
              plot_bgcolor: "rgba(0,0,0,0)",
            }}
            config={{ displayModeBar: false, responsive: true }}
            useResizeHandler={true}
            style={{ width: "100%", height, cursor: onItemClick ? "pointer" : undefined }}
            onClick={(event: PlotMouseEvent) => {
              const id = event.points?.[0]?.customdata as number | string | undefined;
              if (id !== undefined && onItemClick) onItemClick(id);
            }}
          />
        </div>
      )}
    </div>
  );
};
